import getFreeIncludedKeyQuantity from "@secureo/common/utils/lockingSystems/getFreeIncludedKeyQuantity"
import { netToGross } from "@secureo/common/utils/tax"

import {
	LockingSystemChargesByMatrixType,
	LockingSystemCharges,
	LockingSystemMatrixType,
} from "@secureo/common/typings/LockingSystem"
import getMatrixType from "@secureo/common/utils/getMatrixType"

export const getComponentCharges = (
	matrixType: LockingSystemMatrixType,
	lockingSystemChargesByMatrixType: LockingSystemChargesByMatrixType,
) => {
	switch (matrixType) {
		case "single":
		case "simultaneous":
			return lockingSystemChargesByMatrixType?.[matrixType]

		default:
			return lockingSystemChargesByMatrixType?.system
	}
}

export const roundCharges = (chargesCents: number) => Math.round(chargesCents)

export interface MinimalComponentWithCharges {
	uuid: string
	quantity: number
	lockingSystemChargesByMatrixType: LockingSystemChargesByMatrixType
	/**
	 * Single net price cents, without configurations
	 */
	productSingleNetPriceCents: number
}

const getLockingSystemCharges = (
	matrix: boolean[][],
	lockingComponents: MinimalComponentWithCharges[],
	keyComponents: MinimalComponentWithCharges[],
	taxRate: number,
) => {
	const matrixType = getMatrixType(matrix)

	let numberOfFreeIncludedKeys = 0

	const componentChargesByUuid: LockingSystemCharges["byUuid"] = {}

	// Locking components
	for (const lockingComponent of lockingComponents) {
		const { uuid, lockingSystemChargesByMatrixType, quantity } = lockingComponent
		const componentCharges = getComponentCharges(matrixType, lockingSystemChargesByMatrixType)

		numberOfFreeIncludedKeys += componentCharges?.numberOfFreeIncludedKeys ?? 0

		const singleNetChargeCents = componentCharges?.netChargeEuroCents ?? 0
		const singleGrossChargeCents = roundCharges(netToGross(singleNetChargeCents, taxRate))
		const totalNetChargeCents = singleNetChargeCents * quantity
		const totalGrossChargeCents = singleGrossChargeCents * quantity

		componentChargesByUuid[uuid] = {
			totalNetChargeCents,
			totalGrossChargeCents,
		}
	}

	let numberOfRemainingFreeKeys = numberOfFreeIncludedKeys

	// Key components
	for (const keyComponent of keyComponents) {
		const {
			uuid,
			lockingSystemChargesByMatrixType,
			quantity: keyGroupQuantity,
			productSingleNetPriceCents,
		} = keyComponent

		// Key component charges without free keys derived from locking component charges
		const componentCharges = getComponentCharges(matrixType, lockingSystemChargesByMatrixType)
		const baseSingleNetChargeCents = componentCharges?.netChargeEuroCents ?? 0
		const baseSingleGrossChargeCents = roundCharges(
			netToGross(baseSingleNetChargeCents, taxRate),
		)

		// Check how many keys are included for free
		const freeIncludedQuantity = getFreeIncludedKeyQuantity(
			numberOfRemainingFreeKeys,
			keyGroupQuantity,
		)
		const nonFreeIncludedQuantity = keyGroupQuantity - freeIncludedQuantity

		const freeIncludedKeysTotalNetChargesCents =
			productSingleNetPriceCents * freeIncludedQuantity * -1

		// Only calculate charges for non-free keys
		const baseTotalNetChargesCents = baseSingleNetChargeCents * nonFreeIncludedQuantity
		const baseTotalGrossChargesCents = baseSingleGrossChargeCents * nonFreeIncludedQuantity

		/**
		 * Warning: If freeIncludedKeysTotalNetChargesCents is multiplied by freeIncludedQuantity, rounding errors will be introduced
		 *
		 * As a workaround, the productSingleNetPriceCents is converted to gross, rounded, and then multiplied by freeIncludedQuantity
		 */
		const freeIncludedKeysTotalGrossChargesCents =
			roundCharges(netToGross(productSingleNetPriceCents, taxRate)) *
			freeIncludedQuantity *
			-1

		const totalNetChargeCents = baseTotalNetChargesCents + freeIncludedKeysTotalNetChargesCents
		const totalGrossChargeCents =
			baseTotalGrossChargesCents + freeIncludedKeysTotalGrossChargesCents

		componentChargesByUuid[uuid] = {
			totalNetChargeCents,
			totalGrossChargeCents,
			freeIncludedQuantity,
		}

		// Decrease numberOfRemainingFreeKeys by quantity of freeIncludedQuantity in current iteration/key-group
		numberOfRemainingFreeKeys -= freeIncludedQuantity
	}

	const totalNetChargeCents = Object.values(componentChargesByUuid).reduce(
		(totalNetChargeCents, componentCharges) => {
			return totalNetChargeCents + componentCharges.totalNetChargeCents
		},
		0,
	)
	const totalGrossChargeCents = Object.values(componentChargesByUuid).reduce(
		(totalGrossChargeCents, componentCharges) => {
			return totalGrossChargeCents + componentCharges.totalGrossChargeCents
		},
		0,
	)

	const charges: LockingSystemCharges = {
		numberOfFreeIncludedKeys,
		totalNetChargeCents,
		totalGrossChargeCents,
		byUuid: componentChargesByUuid,
	}

	return charges
}

export default getLockingSystemCharges
